import React from "react";
import { NavLink } from "react-router-dom";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl, checkIsActive } from "../../../../helpers";
import { useSelector } from "react-redux";
import { hasRouteAccess } from "../../../../utils/permissions";

export const MenuItem = ({ item, selectedSite, getMenuItemActive }) => {
  const user = useSelector((state) => state.user);

  // Check if any submenu item is visible (for auto-showing parent)
  const hasVisibleSubmenu = () => {
    if (!item.submenu) return false;

    // ADMIN role sees everything
    if (user?.role === "ADMIN") {
      return true;
    }

    // For STAFF and MO, check if any child has permission
    return item.submenu.some((subItem) => {
      if (subItem.isPublic) return true;
      if (subItem.path && hasRouteAccess(user, subItem.path)) return true;
      // If this is a nested submenu, check recursively
      if (subItem.submenu) {
        const hasVisibleChildren = subItem.submenu.some((child) => {
          if (child.isPublic) return true;
          if (child.path && hasRouteAccess(user, child.path)) return true;
          return false;
        });
        if (hasVisibleChildren) return true;
      }
      return false;
    });
  };

  // Check if this item should be visible
  const isVisible = () => {
    // Public routes are always visible
    if (item.isPublic) return true;

    // ADMIN role sees everything
    if (user?.role === "ADMIN") {
      return true;
    }

    // If item has a path, check route access
    if (item.path) {
      return hasRouteAccess(user, item.path);
    }

    // If item has submenu and any child is visible, show parent
    if (item.hasSubmenu && item.submenu) {
      return hasVisibleSubmenu();
    }

    // Default: hide for STAFF/MO if no path
    return false;
  };

  if (!isVisible()) {
    return null;
  }

  // Render menu item with submenu
  if (item.hasSubmenu && item.submenu) {
    // Collect all paths from nested submenus for active state
    const getAllSubmenuPaths = (submenu) => {
      let paths = [];
      submenu.forEach((subItem) => {
        if (subItem.path) {
          paths.push(subItem.path);
        }
        if (subItem.submenu) {
          paths = paths.concat(getAllSubmenuPaths(subItem.submenu));
        }
      });
      return paths;
    };

    const allPaths = getAllSubmenuPaths(item.submenu);
    const activeClasses = allPaths
      .map((path) => getMenuItemActive(path, false))
      .join("");

    return (
      <li
        className={`menu-item menu-item-submenu ${activeClasses}`}
        aria-haspopup="true"
        data-menu-toggle="hover"
      >
        <NavLink className="menu-link menu-toggle" to="#">
          {item.icon ? (
            <span className="svg-icon menu-icon">
              <SVG src={toAbsoluteUrl(item.icon)} />
            </span>
          ) : (
            <i className="menu-bullet menu-bullet-dot">
              <span />
            </i>
          )}
          <span className="menu-text">{item.title}</span>
          {item.hasSubmenu && <i className="menu-arrow" />}
        </NavLink>

        {item.submenu && (
          <div className="menu-submenu ">
            <i className="menu-arrow" />
            <ul className="menu-subnav">
              {item.submenu.map((subItem) => (
                <MenuItem
                  key={subItem.id}
                  item={subItem}
                  selectedSite={selectedSite}
                  getMenuItemActive={getMenuItemActive}
                />
              ))}
            </ul>
          </div>
        )}
      </li>
    );
  }

  // Render simple menu item
  // Check if this item requires siteId parameter
  const path =
    item.requiresSiteId && selectedSite
      ? `${item.path}?siteId=${selectedSite}`
      : item.path;

  return (
    <li
      className={`menu-item ${getMenuItemActive(item.path || "", false)}`}
      aria-haspopup="true"
    >
      <NavLink className="menu-link" to={path}>
        {item.icon && (
          <span className="svg-icon menu-icon">
            <SVG src={toAbsoluteUrl(item.icon)} />
          </span>
        )}
        {!item.icon && (
          <i className="menu-bullet menu-bullet-dot">
            <span />
          </i>
        )}
        <span className="menu-text">{item.title}</span>
      </NavLink>
    </li>
  );
};
